import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';
import { ComparativeStats } from './ComparativeStats';

interface Sale {
  id: string;
  sale_date: string;
  total_amount: number;
  quantity_sold: number;
  product_id: string;
  product_name: string;
  unit_price: number;
  payment_method: string;
}

interface SalesChartProps {
  sales: Sale[];
  customColor?: string;
  title?: string;
  showComparativeStats?: boolean;
}

export const SalesChart: React.FC<SalesChartProps> = ({ sales, customColor, title, showComparativeStats = false }) => {
  // Format price helper
  const formatPrice = (price: number) => 
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);

  // Sales data processing
  const chartData = useMemo(() => {
    // Build a map keyed by full date string (dd/mm/yyyy) to preserve exact day
    const dateMap: Record<string, number> = {};
    sales.forEach(s => {
      const d = new Date(s.sale_date);
      const key = d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      dateMap[key] = (dateMap[key] || 0) + s.total_amount;
    });

    const uniqueDates = Object.keys(dateMap);
    
    // Si une seule date : afficher prix en X et heures en Y  
    if (uniqueDates.length === 1) {
      return sales
        .filter(s => {
          const d = new Date(s.sale_date);
          const key = d.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
          return key === uniqueDates[0];
        })
        .map(s => ({
          time: new Date(s.sale_date).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          price: s.total_amount,
          productName: s.product_name
        }))
        .sort((a, b) => a.time.localeCompare(b.time));
    }

    // Sinon : afficher évolution par date
    return Object.entries(dateMap).map(([date, total]) => ({
      date,
      total
    })).sort((a, b) => {
      const da = new Date(a.date.split('/').reverse().join('-'));
      const db = new Date(b.date.split('/').reverse().join('-'));
      return +da - +db;
    });
  }, [sales]);

  const isSingleDate = useMemo(() => {
    const uniqueDates = new Set(sales.map(s => {
      const d = new Date(s.sale_date);
      return d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }));
    return uniqueDates.size === 1;
  }, [sales]);

  // Custom Sales tooltip
  const SalesTooltip: React.FC<any> = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          {isSingleDate ? (
            <>
              <p className="font-medium text-foreground">{data.time}</p>
              <p className="text-sm text-foreground">Montant : {formatPrice(data.price)}</p>
              <p className="text-xs text-muted-foreground">{data.productName}</p>
            </>
          ) : (
            <>
              <p className="font-medium text-foreground">{data.date}</p>
              <p className="text-sm text-foreground">Total : {formatPrice(data.total)}</p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div 
              className={cn('p-3 rounded-lg transition-transform duration-200 hover:scale-105', 
                !customColor && 'bg-gradient-to-br from-orange-500/10 to-orange-600/20'
              )}
              style={customColor ? { 
                background: `linear-gradient(135deg, ${customColor}10, ${customColor}20)`
              } : undefined}
            >
              <TrendingUp 
                className={cn('w-6 h-6', !customColor && 'text-orange-600')}
                style={customColor ? { color: customColor } : undefined}
              />
            </div>
            <div className="flex-1">
              <p 
                className={cn('text-sm font-medium mb-1', !customColor && 'text-orange-700 dark:text-orange-300')}
                style={customColor ? { color: customColor } : undefined}
              >
                Analyse
              </p>
              <p 
                className={cn('text-xl font-bold', !customColor && 'text-orange-800 dark:text-orange-200')}
                style={customColor ? { color: customColor } : undefined}
              >
                {title || (isSingleDate ? 'Ventes par heure' : 'Évolution des ventes')}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.08} />
              <XAxis 
                dataKey={isSingleDate ? "time" : "date"} 
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                tickFormatter={isSingleDate ? (v) => `${v}€` : (v) => `${v}€`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<SalesTooltip />} />

              <defs>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={customColor || "hsl(var(--primary))"} stopOpacity={1} />
                  <stop offset="100%" stopColor={customColor || "hsl(var(--primary))"} stopOpacity={0.2} />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <Line
                type="monotone"
                dataKey={isSingleDate ? "price" : "total"}
                stroke={customColor || "hsl(var(--primary))"}
                strokeWidth={3}
                dot={isSingleDate ? { r: 4, fill: customColor || "hsl(var(--primary))" } : false}
                filter="url(#glow)"
                isAnimationActive={false}
                id="animatedLine"
              />
            </LineChart>
          </ResponsiveContainer>

          <style>{`
            @keyframes drawLine {
              to {
                stroke-dashoffset: 0;
              }
            }
            #animatedLine {
              stroke-dasharray: 1000;
              stroke-dashoffset: 1000;
              animation: drawLine 2s ease-out forwards;
            }
          `}</style>
        </CardContent>
      </Card>

      {showComparativeStats && (
        <ComparativeStats sales={sales} customColor={customColor} />
      )}
    </div>
  );
};